/**
 * Whether an investment product is visible to customers on Digital Bank.
 *
 * The field is `isCustomerFacing` on a create or update request
 * (`BaseInvestmentProductRequestDto.cs:24`) and `is_customer_facing` on the
 * product response (`InvestmentProductResponseDTO.cs:78-80`). It shipped in
 * migration `20260811191316_kev_add_cols_to_investment_tbls_product_and_investment`.
 *
 * It was previously written here as `productVisibleToCustomer`, behind a mock
 * store, on the understanding that the backend had not shipped it. That name
 * does not exist anywhere in `advanclyservice`.
 *
 * This is not cosmetic: `CreateInvestmentValidator.cs:217` rejects a
 * customer-initiated investment unless the product is customer-facing, so a
 * product left unset is invisible and unbuyable.
 */

/** The key on a create or update request. */
export const PRODUCT_VISIBILITY_FIELD = "isCustomerFacing";

/** The key on a product read back from the API. */
export const PRODUCT_VISIBILITY_RESPONSE_FIELD = "is_customer_facing";

export const PRODUCT_VISIBILITY_LABELS = {
  visible: "Visible to customers",
  hidden: "Not visible to customers",
  unknown: "Not set",
} as const;

/** The copy has to follow the state — it is the only cue that says what "on" does. */
export const PRODUCT_VISIBILITY_DESCRIPTIONS = {
  visible: "Customers can see and subscribe to this product on Digital Bank.",
  hidden: "This product is hidden from customers on Digital Bank.",
} as const;

type TVisibilitySource = Record<string, any> | undefined;

/**
 * The visibility flag carried by a product or a pending update.
 *
 * Reads either spelling, because a pending update echoes back the camelCase
 * key it was sent while a saved product returns the snake_case one.
 *
 * Returns `undefined` when nothing carries the flag — a product the backend
 * has not stamped is not the same thing as a product that is hidden, and the
 * badge says so.
 */
export function resolveProductVisibility(
  source: TVisibilitySource,
): boolean | undefined {
  const value =
    source?.[PRODUCT_VISIBILITY_RESPONSE_FIELD] ??
    source?.[PRODUCT_VISIBILITY_FIELD];

  return typeof value === "boolean" ? value : undefined;
}
